import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import { withRequiredRole } from "src/ui/navigation/utils/withRequiredRole";
import { DangerButton, PrimaryButton } from "src/ui/shared/CustomButton";
import { TextField } from "src/ui/shared/form/TextField";
import {
    MinimalTable,
    MinimalTableCell,
    MinimalTableHeader,
    MinimalTableHeaderCell,
} from "src/ui/shared/table/MinimalTableComponents";
import { Header } from "src/ui/shared/table/TableHeader";

import { useFetchAccounts } from "@admin/hooks/useFetchAccounts";
import { useFetchRoles } from "@admin/hooks/useFetchRoles";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { SectionContainer, SectionHeader } from "@dashboard/shared/styles";
import { Box, Paper, TableContainer, TableRow } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { TableBody } from "@shared/table/TableBody";
import CustomTablePagination from "@shared/table/TablePagination";
import { showErrorToast } from "@shared/toasts/Toasts";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { assignRole, FetchAccountsPayload, removeRole } from "../../api/account";

import { useStyles } from "./AdminRolesPage.style";

type FormValues = {
    id: string;
    role: string;
    action: "add" | "remove";
};

const initialValues: FormValues = {
    id: "",
    role: EmteriaRoles.EmteriaAccountantRole,
    action: "add",
};

const defaultParams: Required<FetchAccountsPayload> = {
    PageIndex: 0,
    PageSize: 12,
    SortCriteria: "email",
    SortDirection: 0,
    HasRoles: true,
    ExcludeDeleted: false,
};

const columnHelper = createColumnHelper<API.Account>();

const tableColumns = [
    columnHelper.accessor("id", {
        id: "id",
        header: i18next.t("common:label.id"),
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
    }),
    columnHelper.accessor("email", {
        id: "mail",
        header: i18next.t("common:label.email"),
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
    }),
    columnHelper.accessor("name", {
        id: "name",
        header: i18next.t("common:label.name"),
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
    }),
    columnHelper.accessor("roles", {
        id: "role",
        header: i18next.t("common:label.role"),
        cell: (info) => <MinimalTableCell>{info.getValue().join(", ")}</MinimalTableCell>,
    }),
];

const AdminRolesPage = () => {
    const [params, setParams] = React.useState<Required<FetchAccountsPayload>>(defaultParams);
    const { data: accounts, isLoading: isLoadingAccounts, count: accountsCount, setData: setAccounts } = useFetchAccounts(params);
    const { data: roles, isLoading: isLoadingRoles } = useFetchRoles();

    const { classes } = useStyles();
    const { t } = useTranslation(["common", "pages"]);

    const roleOptions = roles?.map((value) => ({ label: value, value })) || [];
    const actionOptions = [
        { label: t("common:button.add"), value: "add" },
        { label: t("common:button.remove"), value: "remove" },
    ];

    const table = useReactTable<API.Account>({
        data: accounts,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
    });

    const onAssignRole = async (account: API.Account, role: string) => {
        try {
            await assignRole({ id: account.id, role });
            setAccounts((prev) => [...prev, { ...account, roles: [...account.roles, role] }]);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:adminRolesPage.toast.assignRoleError"));
        }
    };

    const onRemoveRole = async (account: API.Account, role: string) => {
        try {
            await removeRole({ id: account.id, role });
            const newRoles = account.roles.filter((item) => item !== role);
            setAccounts((prev) => prev.map((item) => (item.id === account.id ? { ...item, roles: newRoles } : item)));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:adminRolesPage.toast.removeRoleError"));
        }
    };

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const account = accounts.find((item) => item.id === values.id);

        if (!account) {
            showErrorToast(t("pages:adminRolesPage.toast.accountNotFound"));
            return;
        }

        if (values.action === "add") {
            await onAssignRole(account, values.role);
        }

        if (values.action === "remove") {
            await onRemoveRole(account, values.role);
        }

        form.restart();
    };

    const isLoading = isLoadingAccounts || isLoadingRoles;

    return (
        <PageContainer>
            <Form<FormValues>
                onSubmit={onSubmit}
                initialValues={initialValues}
                render={({ handleSubmit, values, submitting }) => (
                    <form onSubmit={handleSubmit}>
                        <SectionContainer className={classes.section}>
                            <SectionHeader>{t("pages:adminRolesPage.changeRole")}</SectionHeader>
                            <Box className={classes.formBox}>
                                <TextField name="id" label={t("pages:adminRolesPage.userId")} />
                                <ControlledSelectField name="role" label={t("pages:adminRolesPage.role")} options={roleOptions} />
                                <ControlledSelectField name="action" label={t("common:label.action")} options={actionOptions} />
                            </Box>
                            <Box className={classes.buttonBox}>
                                {values?.action === "remove" ? (
                                    <DangerButton type="submit" disabled={submitting} loading={submitting}>
                                        {t("common:button.remove")}
                                    </DangerButton>
                                ) : (
                                    <PrimaryButton type="submit" disabled={submitting} loading={submitting}>
                                        {t("common:button.add")}
                                    </PrimaryButton>
                                )}
                            </Box>
                        </SectionContainer>
                    </form>
                )}
            />

            <SectionHeader>{t("pages:adminRolesPage.existingRoles")}</SectionHeader>
            <TableContainer component={Paper}>
                <MinimalTable>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                                <MinimalTableHeaderCell width={80}></MinimalTableHeaderCell>
                            </TableRow>
                        ))}
                    </MinimalTableHeader>

                    <TableBody table={table} loading={isLoading} noDataMessage={t("pages:adminRolesPage.noRolesToDisplay")}>
                        {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) =>
                                    flexRender(cell.column.columnDef.cell, {
                                        ...cell.getContext(),
                                        key: cell.id,
                                    }),
                                )}
                                <MinimalTableCell></MinimalTableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>
            <CustomTablePagination
                component="div"
                rowsPerPageOptions={[12, 24, 48]}
                count={accountsCount}
                rowsPerPage={params.PageSize}
                page={params.PageIndex}
                onPageChange={(_event, page) => {
                    setParams({ ...params, PageIndex: page });
                }}
                onRowsPerPageChange={(e) => {
                    setParams({ ...params, PageSize: +e.target.value });
                }}
            />
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminRolesPage, {
        title: i18next.t("pages:adminRolesPage.metaTitle"),
        description: i18next.t("pages:adminRolesPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
